/**
 * Centralisation des messages d'erreur d'authentification.
 *
 * Source unique de vérité pour traduire une réponse HTTP (ou une erreur réseau)
 * en message utilisateur clair, en français.
 *
 * Convention de réponse côté backend :
 *   - 200  : succès
 *   - 400  : requête invalide (champs manquants / mal formés)
 *   - 401  : identifiants invalides
 *   - 403  : accès refusé (ex. email non vérifié, compte désactivé)
 *   - 404  : ressource introuvable
 *   - 409  : conflit (ex. email déjà utilisé)
 *   - 423  : compte verrouillé
 *   - 429  : trop de tentatives
 *   - 5xx  : erreur serveur
 *
 * Le backend peut enrichir la réponse JSON avec un champ `code` (string)
 * permettant un mapping plus fin que le seul code HTTP. Exemple :
 *   { "code": "EMAIL_NOT_VERIFIED", "message": "..." }
 */

/**
 * Codes métier renvoyés par le backend (champ `code` dans le JSON).
 * Garde cette liste synchronisée avec Life_backend/user/views.py.
 */
export const AUTH_ERROR_CODES = Object.freeze({
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  EMAIL_INVALID: 'EMAIL_INVALID',
  PASSWORD_TOO_WEAK: 'PASSWORD_TOO_WEAK',
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  RATE_LIMITED: 'RATE_LIMITED',
  TIMEOUT: 'TIMEOUT',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN',
})

/**
 * Mapping code métier → message français destiné à l'utilisateur final.
 * Ces messages sont volontairement neutres (pas de divulgation d'info sensible).
 */
const MESSAGES = Object.freeze({
  [AUTH_ERROR_CODES.INVALID_CREDENTIALS]:
    'Email ou mot de passe incorrect.',
  [AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED]:
    "Votre adresse email n'a pas encore été vérifiée.",
  [AUTH_ERROR_CODES.ACCOUNT_LOCKED]:
    'Ce compte est temporairement verrouillé. Réessayez dans quelques minutes.',
  [AUTH_ERROR_CODES.ACCOUNT_DISABLED]:
    'Ce compte est désactivé. Contactez le support.',
  [AUTH_ERROR_CODES.EMAIL_ALREADY_EXISTS]:
    'Un compte existe déjà avec cette adresse email.',
  [AUTH_ERROR_CODES.EMAIL_INVALID]:
    "Cette adresse email n'est pas valide.",
  [AUTH_ERROR_CODES.PASSWORD_TOO_WEAK]:
    'Le mot de passe est trop faible.',
  [AUTH_ERROR_CODES.CONSENT_REQUIRED]:
    'Vous devez accepter les Conditions Générales et la Politique de Confidentialité.',
  [AUTH_ERROR_CODES.RATE_LIMITED]:
    'Trop de tentatives. Patientez quelques minutes avant de réessayer.',
  [AUTH_ERROR_CODES.TIMEOUT]:
    'Le serveur met trop de temps à répondre. Vérifiez votre connexion ou réessayez.',
  [AUTH_ERROR_CODES.NETWORK_ERROR]:
    "Connexion impossible au serveur. Vérifiez votre connexion internet et réessayez.",
  [AUTH_ERROR_CODES.SERVER_ERROR]:
    "Une erreur est survenue de notre côté. Réessayez dans quelques instants.",
  [AUTH_ERROR_CODES.UNKNOWN]:
    "Une erreur inattendue est survenue. Réessayez.",
})

/**
 * Mapping fallback code HTTP → code métier, utilisé quand le backend
 * ne renvoie pas de champ `code` explicite.
 */
const HTTP_TO_CODE = Object.freeze({
  400: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
  401: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
  403: AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED,
  404: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
  409: AUTH_ERROR_CODES.EMAIL_ALREADY_EXISTS,
  423: AUTH_ERROR_CODES.ACCOUNT_LOCKED,
  429: AUTH_ERROR_CODES.RATE_LIMITED,
  500: AUTH_ERROR_CODES.SERVER_ERROR,
  502: AUTH_ERROR_CODES.SERVER_ERROR,
  503: AUTH_ERROR_CODES.SERVER_ERROR,
  504: AUTH_ERROR_CODES.SERVER_ERROR,
})

/**
 * Transforme une réponse axios (success ou error) en objet {code, message}.
 *
 * Usage :
 *   const response = await makePostRequest(urls.login, payload, {}, false)
 *   if (response.status !== 200) {
 *     const { code, message } = parseAuthError(response)
 *     setError({ code, message })
 *   }
 *
 * @param {object|undefined} response - réponse axios (response.data, response.status)
 *                                       ou undefined si erreur réseau
 * @param {string} [fallbackContext] - 'login' | 'register' | 'reset' pour
 *                                     ajuster le mapping HTTP par défaut
 * @returns {{ code: string, message: string, status: number|null }}
 */
export function parseAuthError(response, fallbackContext = 'login') {
  // Cas erreur réseau : axios renvoie undefined dans notre wrapper
  if (!response) {
    return {
      code: AUTH_ERROR_CODES.NETWORK_ERROR,
      message: MESSAGES[AUTH_ERROR_CODES.NETWORK_ERROR],
      status: null,
    }
  }

  const { status, data } = response

  // Le backend a fourni un code métier explicite : on lui fait confiance
  if (data && typeof data === 'object' && data.code && MESSAGES[data.code]) {
    return {
      code: data.code,
      message: data.message || MESSAGES[data.code],
      status,
    }
  }

  // Fallback : on déduit du code HTTP
  let code = HTTP_TO_CODE[status] || AUTH_ERROR_CODES.UNKNOWN

  // Ajustements contextuels (ex. en register, un 400 = validation, pas creds)
  if (fallbackContext === 'register' && status === 400) {
    code = AUTH_ERROR_CODES.CONSENT_REQUIRED
  }

  return {
    code,
    message: (data && data.message) || MESSAGES[code],
    status,
  }
}

/**
 * Helper pratique : renvoie juste le message FR à afficher.
 */
export function getAuthErrorMessage(response, fallbackContext = 'login') {
  return parseAuthError(response, fallbackContext).message
}

export default {
  AUTH_ERROR_CODES,
  parseAuthError,
  getAuthErrorMessage,
}
